import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Shared UI Library Components
import { THeaderInfoBlock } from '@ppmcore/seven-ppm-core-shared-components-react';

// Models
import {
  ICompany,
  ICompanyFee,
  ICountry,
  ILanguage,
  ISpeciality,
  ISpecialization
} from '../../interfaces/core.interfaces';

export interface ICoreState {
  infoBlockType: THeaderInfoBlock;
  path: {
    current: string;
    prev: string;
  },
  companyFee: {
    values: ICompanyFee | null;
    loading: boolean;
    error: string | null;
  },
  languages: {
    list: ILanguage[];
    isLoad: boolean;
    isError: boolean;
  };
  countries: {
    list: ICountry[];
    isLoad: boolean;
    isError: boolean;
  };
  speciality: {
    list: ISpeciality[];
    isLoad: boolean;
    isError: boolean;
  };
  specializations: {
    list: ISpecialization[];
    isLoad: boolean;
    isError: boolean;
  };
  companies: {
    list: ICompany[];
    isLoad: boolean;
    isError: boolean;
  }
}

const initialState: ICoreState = {
  infoBlockType: 'base',
  path: {
    current: '',
    prev: '',
  },
  companyFee: {
    values: null,
    loading: true,
    error: null
  },
  languages: {
    list: [],
    isLoad: true,
    isError: false
  },
  countries: {
    list: [],
    isLoad: true,
    isError: false
  },
  speciality: {
    list: [],
    isLoad: false,
    isError: false
  },
  specializations: {
    list: [],
    isLoad: false,
    isError: false
  },
  companies: {
    list: [],
    isLoad: false,
    isError: false
  }
};

export const coreSlice = createSlice({
  name: 'core',
  initialState,
  reducers: {
    setHeaderInfoBlockType: (state, { payload }: PayloadAction<THeaderInfoBlock>) => {
      state.infoBlockType = payload;
    },
    setCurrentPath: (state, { payload }: PayloadAction<string>) => {
      state.path = { current: payload, prev: state.path.current };
    },

    loadCompanyFee: (state) => {
      state.companyFee = {
        ...state.companyFee,
        loading: true,
        error: null,
      };
    },
    loadCompanyFeeSuccess: (state, { payload }: PayloadAction<ICompanyFee>) => {
      state.companyFee = {
        values: payload,
        loading: false,
        error: null,
      };
    },
    loadCompanyFeeError: (state, { payload: error }: PayloadAction<string>) => {
      state.companyFee = {
        ...state.companyFee,
        error,
      };
    },

    loadLanguages: (state) => {
      state.languages = { ...state.languages, isLoad: true, isError: false };
    },
    loadLanguagesSuccess: (state, { payload }: PayloadAction<ILanguage[]>) => {
      state.languages = { list: payload, isLoad: false, isError: false };
    },
    loadLanguagesError: (state) => {
      state.languages = { ...state.languages, isLoad: false, isError: true };
    },

    loadCountries: (state) => {
      state.countries = { ...state.countries, isLoad: true, isError: false };
    },
    loadCountriesSuccess: (state, { payload }: PayloadAction<ICountry[]>) => {
      state.countries = { list: payload, isLoad: false, isError: false };
    },
    loadCountriesError: (state) => {
      state.countries = { ...state.countries, isLoad: false, isError: true };
    },

    loadSpecialities: (state) => {
      state.speciality = { ...state.speciality, isLoad: true, isError: false };
    },
    loadSpecialitiesSuccess: (state, { payload }: PayloadAction<ISpeciality[]>) => {
      state.speciality = { list: payload, isLoad: false, isError: false };
    },
    loadSpecialitiesError: (state) => {
      state.speciality = { ...state.speciality, isLoad: false, isError: true };
    },

    loadSpecializations: (state) => {
      state.specializations = { ...state.specializations, isLoad: true, isError: false };
    },
    loadSpecializationsSuccess: (state, { payload }: PayloadAction<ISpecialization[]>) => {
      state.specializations = { list: payload, isLoad: false, isError: false };
    },
    loadSpecializationsError: (state) => {
      state.specializations = { ...state.specializations, isLoad: false, isError: true };
    },
    clearSpecializations: (state) => {
      state.specializations = { list: [], isLoad: false, isError: false };
    },

    loadCompanies: (state) => {
      state.companies = { ...state.companies, isLoad: true, isError: false };
    },
    loadCompaniesSuccess: (state, { payload }: PayloadAction<ICompany[]>) => {
      state.companies = { list: payload, isLoad: false, isError: false };
    },
    loadCompaniesError: (state) => {
      state.companies = { ...state.companies, isLoad: false, isError: true };
    },
  }
});

export const CoreSliceActions = coreSlice.actions;
